import React, { useState, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import { Box, CircularProgress, Typography, LinearProgress } from '@mui/material';
import { Logo } from './logo';

// DOM readiness utility
const waitForDOMReady = () => {
    return new Promise((resolve) => {
        if (document.readyState === 'complete') {
            resolve();
        } else {
            const handleLoad = () => {
                document.removeEventListener('DOMContentLoaded', handleLoad);
                window.removeEventListener('load', handleLoad);
                resolve();
            };
            document.addEventListener('DOMContentLoaded', handleLoad);
            window.addEventListener('load', handleLoad);
        }
    });
};

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const SplashScreen = ({ onReady, shouldShow = true }) => {
    const [progress, setProgress] = useState(0);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [domReady, setDomReady] = useState(false);
    const [assetsLoaded, setAssetsLoaded] = useState(false);

    // Check for DOM readiness and critical elements
    useEffect(() => {
        const checkDOMAndAssets = async () => {
            try {
                // Wait for DOM to be ready
                await waitForDOMReady();
                setLoadingText('DOM ready, loading assets...');

                // Wait for critical elements to be available
                const checkCriticalElements = () => {
                    return new Promise((resolve) => {
                        const checkElements = () => {
                            // Check for critical elements that should be loaded
                            const criticalSelectors = [
                                'head', 'body', 'main', '[data-testid]', '.MuiTheme-root'
                            ];

                            let elementsFound = 0;
                            criticalSelectors.forEach(selector => {
                                if (document.querySelector(selector)) {
                                    elementsFound++;
                                }
                            });

                            // If most critical elements are found, consider DOM ready
                            if (elementsFound >= 2) {
                                resolve();
                            } else {
                                setTimeout(checkElements, 100);
                            }
                        };
                        checkElements();
                    });
                };

                await checkCriticalElements();
                setDomReady(true);
                setLoadingText('Loading application resources...');

                // Simulate asset loading check
                setTimeout(() => {
                    setAssetsLoaded(true);
                    setLoadingText('Almost ready...');
                }, 500);

            } catch (error) {
                console.warn('DOM readiness check failed:', error);
                // Fallback: proceed anyway after a delay
                setTimeout(() => {
                    setDomReady(true);
                    setAssetsLoaded(true);
                }, 1000);
            }
        };

        checkDOMAndAssets();
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setProgress((prevProgress) => {
                if (prevProgress >= 100) {
                    return 100;
                }

                // Slow down progress if DOM/assets aren't ready
                let maxIncrement = 2;
                if (domReady && assetsLoaded) {
                    maxIncrement = 10; // Speed up when everything is ready
                } else if (domReady) {
                    maxIncrement = 5; // Medium speed when DOM is ready
                }

                const increment = Math.random() * maxIncrement + 1;
                const newProgress = prevProgress + increment;
                return newProgress >= 100 ? 100 : newProgress;
            });
        }, 150);

        const textTimer = setInterval(() => {
            if (!domReady) {
                const texts = ['Loading...', 'Preparing DOM...', 'Initializing...'];
                setLoadingText(texts[Math.floor(Math.random() * texts.length)]);
            } else if (!assetsLoaded) {
                const texts = ['Loading assets...', 'Preparing resources...', 'Setting up...'];
                setLoadingText(texts[Math.floor(Math.random() * texts.length)]);
            } else {
                const texts = ['Almost ready...', 'Finalizing...', 'Ready to launch!'];
                setLoadingText(texts[Math.floor(Math.random() * texts.length)]);
            }
        }, 1500);

        // Only allow completion when DOM and assets are ready
        const forceCompleteTimer = setTimeout(() => {
            if (domReady && assetsLoaded) {
                setProgress(100);
                // Notify parent component that we're ready
                if (onReady) {
                    setTimeout(() => onReady(), 500);
                }
            }
        }, domReady && assetsLoaded ? 2000 : 5000); // Longer timeout if not ready

        return () => {
            clearInterval(timer);
            clearInterval(textTimer);
            clearTimeout(forceCompleteTimer);
        };
    }, [domReady, assetsLoaded, onReady]);

    // If shouldShow is false, don't render the splash screen
    if (!shouldShow) {
        return null;
    }

    return (
        <Box
            sx={{
                alignItems: 'center',
                background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)', // Teal gradient
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                justifyContent: 'center',
                left: 0,
                p: 3,
                position: 'fixed',
                top: 0,
                width: '100vw',
                zIndex: 2000,
                animation: `${fadeIn} 0.5s ease-out`
            }}
        >
            {/* Logo with pulse animation */}
            <Box
                sx={{
                    mb: 6,
                    animation: `${pulse} 2s ease-in-out infinite`,
                    filter: "drop-shadow(0px 10px 20px rgba(0,0,0,0.2))"
                }}
            >
                <Logo
                    sx={{
                        height: 100,
                        width: 100,
                        color: "#FFFFFF"
                    }}
                />
            </Box>

            {/* Circular Progress Spinner */}
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                }}
            >
                <CircularProgress
                    variant="determinate"
                    value={progress}
                    size={80}
                    thickness={4}
                    sx={{
                        color: '#FFFFFF',
                        opacity: 0.3,
                        position: 'absolute',
                    }}
                />
                <CircularProgress
                    variant="determinate"
                    value={progress}
                    size={80}
                    thickness={4}
                    sx={{
                        color: '#FFFFFF',
                        '& .MuiCircularProgress-circle': {
                            strokeLinecap: 'round'
                        }
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{ fontWeight: 'bold', color: "#FFFFFF", fontSize: "1.2rem" }}
                    >
                        {`${Math.round(progress)}%`}
                    </Typography>
                </Box>
            </Box>

            {/* Loading Text */}
            <Typography
                variant="body1"
                sx={{
                    mb: 2,
                    textAlign: 'center',
                    minHeight: '20px',
                    color: "rgba(255,255,255,0.9)",
                    fontWeight: 500
                }}
            >
                {loadingText}
            </Typography>

            {/* Linear Progress Bar */}
            <Box sx={{ width: '240px', mt: 2 }}>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: '#FFFFFF'
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

export default SplashScreen;